import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import { formatDate } from "../utils/dates";
import { switchToShop, freeSlotIn } from "../utils/shopSwitch";
import Modal from "./Modal";
import Chevron from "./Chevron";
import OpenAnotherShop, { usePackRoom } from "./OpenAnotherShop";

/**
 * The shop's name at the top of the drawer. For someone who works in several
 * shops (and has been given shop switching) it opens the list of their shops,
 * and for an owner whose pack has room, "Open another shop" under it; for
 * everyone else it is just the name, as it always was.
 *
 * Switching is a page reload onto the other shop (utils/shopSwitch.js): every
 * screen, the till included, then starts on the new shop's own data.
 *
 * The list is rendered into document.body: the drawer it sits in slides with a
 * transform, which would otherwise become the containing block of the dialog's
 * `position: fixed` and squeeze it into the drawer.
 */
const ShopSwitcher = () => {
  const { t } = useI18n();
  const { shop, flag } = useMembership();
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);
  const room = usePackRoom();

  const enabled = flag("multi_shop");

  useEffect(() => {
    if (!enabled) return;
    shopsAPI
      .mine()
      .then(({ data }) => setShops(Array.isArray(data) ? data : []))
      .catch(() => setShops([]));
  }, [enabled]);

  const name = <span className="truncate">💨 {shop?.name || "…"}</span>;

  if (!enabled || (shops.length < 2 && !room?.can_add)) {
    return <h1 className="text-xl font-bold text-white truncate pe-2">{name}</h1>;
  }

  const go = async (target) => {
    setBusy(target.id);
    setProblem(null);
    try {
      await switchToShop(target);
    } catch (error) {
      setBusy(null);
      setProblem({ target, error });
    }
  };

  const free = async (target, deviceId) => {
    try {
      await freeSlotIn(target.id, deviceId);
      await go(target);
    } catch {
      setProblem({ target, error: new Error("failed") });
    }
  };

  const message = (() => {
    if (!problem) return null;
    const { error, target } = problem;
    if (error.code === "UNSENT_SALES") return t("shops.switcher.unsentSales", { count: error.details.count });
    if (error.code === "DEVICE_LIMIT") {
      return t("shops.switcher.deviceLimit", { limit: error.details.device_limit, name: target.name });
    }
    return error.response?.data?.message || error.response?.data?.error || t("shops.switcher.failed");
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title={t("shops.switcher.open")}
        className="min-w-0 flex items-center gap-2 text-xl font-bold text-white hover:text-primary-100 text-start pe-2"
      >
        {name}
        <Chevron direction="down" className="shrink-0 text-primary-200" />
      </button>

      {open && createPortal(
        <Modal
          title={t("shops.switcher.title")}
          onClose={() => {
            setOpen(false);
            setProblem(null);
          }}
          maxWidthClass="max-w-md"
          zClass="z-[60]"
        >
          <p className="text-sm text-gray-600 mb-4">{t("shops.switcher.intro")}</p>

          <ul className="space-y-2">
            {shops.map((entry) => {
              const current = entry.id === shop?.id;
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    disabled={current || Boolean(busy)}
                    onClick={() => go(entry)}
                    className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-start ${
                      current
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:border-primary-400 hover:bg-gray-50"
                    } disabled:cursor-default`}
                  >
                    <span className="min-w-0">
                      <span className="block font-semibold text-gray-900 truncate">{entry.name}</span>
                      <span className="block text-xs text-gray-500" dir="ltr">/{entry.slug}</span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold">
                      {current ? (
                        <span className="text-primary-700">{t("shops.switcher.current")}</span>
                      ) : busy === entry.id ? (
                        <span className="text-gray-500">{t("shops.switcher.opening", { name: entry.name })}</span>
                      ) : entry.is_active === false ? (
                        <span className="text-red-600">{t("shops.switcher.suspended")}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-3">
            <OpenAnotherShop room={room} />
          </div>

          {message && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              <p>{message}</p>
              {problem.error.code === "DEVICE_LIMIT" && (
                <ul className="mt-2 space-y-1">
                  {(problem.error.details.devices || []).map((device) => (
                    <li key={device.id} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate">
                        {device.label}
                        {device.last_seen && (
                          <span className="text-amber-700">
                            {" · "}
                            {t("shops.switcher.lastUsed", { when: formatDate(device.last_seen) })}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => free(problem.target, device.id)}
                        className="shrink-0 rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
                      >
                        {t("shops.switcher.free")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Modal>,
        document.body,
      )}
    </>
  );
};

export default ShopSwitcher;
